import React from 'react';
import { Alert, Box, Button, Collapse, IconButton, Stack, Switch, Typography } from '@mui/material';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import AddAlarmOutlinedIcon from '@mui/icons-material/AddAlarmOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import {
  enableMedicationReminderNotifications,
  isMedicationReminderEnabledForChild,
  loadUserNotificationSettings,
  saveMedicationReminderForChild,
} from '../../services/notifications/userNotificationSettings';
import {
  configureMedicationReminder,
  recordMedicationAdministration,
  subscribeToMedicationOccurrences,
  subscribeToMedicationReminderSchedules,
  subscribeToMedicationReminderRounds,
} from '../../services/medicationReminderService';
import DailyMedicationCheck from './DailyMedicationCheck';
import MedicationReminderScheduleEditor from './MedicationReminderScheduleEditor';
import MedicationRoundSetup from './MedicationRoundSetup';
import {
  buildInitialReminderSchedule,
  formatReminderSchedule,
  reminderScheduleNeedsConfirmation,
} from './medicationReminderScheduleAdapter';

const localDayKey = () => {
  const date = new Date();
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
};

const getDeepLinkedOccurrenceId = () => {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('medicationOccurrence') || '';
};

const findNextReminderTime = (rounds, schedules, now = new Date()) => {
  const times = [
    ...rounds.filter((round) => round.active !== false).map((round) => round.reminderTime),
    ...schedules.filter((schedule) => schedule.active && schedule.kind !== 'prn' && !schedule.paused).flatMap((schedule) => schedule.times || []),
  ].filter((time) => /^\d{2}:\d{2}$/.test(time));
  if (!times.length) return '';
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const next = times.map((time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const value = hours * 60 + minutes;
    return { time, distance: (value - currentMinutes + 1440) % 1440 };
  }).sort((a, b) => a.distance - b.distance)[0]?.time;
  if (!next) return '';
  const [hours, minutes] = next.split(':').map(Number);
  return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const MedicationRemindersPanel = ({ childId, childName, medications, canManage, summaryOnly = false, pageLayout = false, onOpen, focusedMedicationId = '', onReminderSaved }) => {
  const initiallyFocusedMedication = medications.find((item) => item.id === focusedMedicationId && !item.isArchived);
  const [open, setOpen] = React.useState(pageLayout || Boolean(getDeepLinkedOccurrenceId()));
  const [enabled, setEnabled] = React.useState(false);
  const [schedules, setSchedules] = React.useState([]);
  const [occurrences, setOccurrences] = React.useState([]);
  const [rounds, setRounds] = React.useState([]);
  const [editingId, setEditingId] = React.useState(canManage && initiallyFocusedMedication ? focusedMedicationId : '');
  const [draft, setDraft] = React.useState(canManage && initiallyFocusedMedication ? { ...buildInitialReminderSchedule(initiallyFocusedMedication), active: true } : null);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [schedulesReady, setSchedulesReady] = React.useState(false);
  const [roundsReady, setRoundsReady] = React.useState(false);
  const handledFocusRef = React.useRef('');
  const focusedOccurrenceId = getDeepLinkedOccurrenceId();

  React.useEffect(() => {
    let active = true;
    loadUserNotificationSettings().then((settings) => {
      if (active) setEnabled(isMedicationReminderEnabledForChild(settings, childId));
    }).catch(() => {});
    const stopSchedules = subscribeToMedicationReminderSchedules(childId, (items) => { setSchedules(items); setSchedulesReady(true); }, (nextError) => setError(nextError.message));
    const stopOccurrences = subscribeToMedicationOccurrences(childId, setOccurrences, (nextError) => setError(nextError.message));
    const stopRounds = subscribeToMedicationReminderRounds(childId, (items) => { setRounds(items); setRoundsReady(true); }, (nextError) => setError(nextError.message));
    return () => { active = false; stopSchedules(); stopOccurrences(); stopRounds(); };
  }, [childId]);

  React.useEffect(() => {
    if (!focusedOccurrenceId || !open) return;
    window.setTimeout(() => document.getElementById(`medication-occurrence-${focusedOccurrenceId}`)?.scrollIntoView({ block: 'center' }), 0);
  }, [focusedOccurrenceId, occurrences, open]);

  const scheduleByMedicationId = React.useMemo(() => new Map(schedules.map((item) => [item.medicationId, item])), [schedules]);
  const activeMedications = React.useMemo(() => medications.filter((item) => !item.isArchived), [medications]);
  const configuredCount = activeMedications.filter((item) => scheduleByMedicationId.get(item.id)?.active).length;
  const activeRoundCount = rounds.filter((round) => round.active !== false).length;
  const nextReminderTime = findNextReminderTime(rounds, schedules);
  const roundMedicationIds = React.useMemo(() => new Set(rounds.flatMap((round) => (round.members || []).map((item) => item.medicationId))), [rounds]);
  const asNeededMedications = activeMedications.filter((medication) => {
    const schedule = scheduleByMedicationId.get(medication.id);
    return schedule?.kind === 'prn' || medication.scheduleType === 'prn' || medication.scheduleType === 'as_needed';
  });
  const individualReminderMedications = activeMedications.filter((medication) => {
    const schedule = scheduleByMedicationId.get(medication.id);
    return schedule?.active && schedule.kind !== 'prn' && !roundMedicationIds.has(medication.id);
  });
  const medicationsWithoutReminder = activeMedications.filter((medication) => {
    const schedule = scheduleByMedicationId.get(medication.id);
    return !schedule?.active && !asNeededMedications.some((item) => item.id === medication.id) && !roundMedicationIds.has(medication.id);
  });
  const todayOccurrences = occurrences
    .filter((item) => item.scheduledDayKey === localDayKey())
    .sort((a, b) => (a.scheduledFor?.getTime?.() || 0) - (b.scheduledFor?.getTime?.() || 0));

  React.useEffect(() => {
    if (enabled && configuredCount === 0 && canManage && activeMedications.length > 0) setOpen(true);
  }, [activeMedications.length, canManage, configuredCount, enabled]);

  const toggleGlobal = async () => {
    setBusy('global'); setError(''); setSuccess('');
    try {
      if (enabled) {
        await saveMedicationReminderForChild(childId, false);
        setEnabled(false);
        setSuccess('Medication notifications are off. Schedules and history were kept.');
      } else {
        const result = await enableMedicationReminderNotifications(childId);
        if (!result.success) {
          const denied = ['denied', 'default'].includes(result.status);
          setError(denied ? 'Notifications are blocked on this device. You can still use Today to record medication.' : 'Notifications could not be enabled on this device. Today tracking is still available.');
        } else {
          setEnabled(true);
          setSuccess('Medication notifications are on for this device.');
        }
      }
    } catch (nextError) { setError(nextError.message); } finally { setBusy(''); }
  };

  const editSchedule = React.useCallback((medication) => {
    const existing = scheduleByMedicationId.get(medication.id);
    setEditingId(medication.id);
    setDraft({ ...(existing || buildInitialReminderSchedule(medication)), active: true });
  }, [scheduleByMedicationId]);

  React.useEffect(() => {
    if (!focusedMedicationId || !schedulesReady || !roundsReady || handledFocusRef.current === focusedMedicationId) return;
    if (rounds.some((round) => (round.members || []).some((member) => member.medicationId === focusedMedicationId))) {
      setEditingId('');
      setDraft(null);
      handledFocusRef.current = focusedMedicationId;
      return;
    }
    const medication = activeMedications.find((item) => item.id === focusedMedicationId);
    if (!medication || !canManage) return;
    handledFocusRef.current = focusedMedicationId;
    editSchedule(medication);
  }, [activeMedications, canManage, editSchedule, focusedMedicationId, rounds, roundsReady, schedulesReady]);

  React.useEffect(() => {
    if (!focusedMedicationId || editingId !== focusedMedicationId) return;
    const timer = window.setTimeout(() => {
      document.getElementById(`medication-reminder-${focusedMedicationId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [editingId, focusedMedicationId]);

  const saveSchedule = async (scheduleToSave = draft) => {
    setBusy(editingId); setError(''); setSuccess('');
    try {
      await configureMedicationReminder(editingId, scheduleToSave);
      if (!enabled) {
        const notificationResult = await enableMedicationReminderNotifications(childId);
        if (notificationResult?.success) setEnabled(true);
      }
      setSuccess('Reminder schedule saved.');
      setEditingId(''); setDraft(null);
      onReminderSaved?.({ medicationId: editingId });
    } catch (nextError) { setError(nextError.message); } finally { setBusy(''); }
  };

  const disableSchedule = async (schedule) => {
    setBusy(schedule.medicationId); setError('');
    try { await configureMedicationReminder(schedule.medicationId, { ...schedule, active: false }); }
    catch (nextError) { setError(nextError.message); }
    finally { setBusy(''); }
  };

  const record = async (occurrenceId, administration) => {
    setBusy(occurrenceId); setError(''); setSuccess('');
    try {
      const result = await recordMedicationAdministration(occurrenceId, administration);
      setSuccess(result.status === 'taken' ? 'Recorded as taken.' : result.status === 'missed' ? 'Recorded as missed.' : 'Recorded as not taken.');
    } catch (nextError) { setError(nextError.message); } finally { setBusy(''); }
  };

  const renderMedicationRows = (items, actionOnly = false) => (
    <Stack spacing={0} sx={{ border: items.length ? '1px solid #DCE5E1' : 0, borderRadius: 2.5, overflow: 'hidden' }}>
      {items.map((medication) => {
        const savedSchedule = scheduleByMedicationId.get(medication.id);
        const schedule = savedSchedule || buildInitialReminderSchedule(medication);
        const needsConfirmation = reminderScheduleNeedsConfirmation(schedule);
        const reminderActionLabel = savedSchedule ? `Edit reminder for ${medication.name || 'medication'}` : `Set up reminder for ${medication.name || 'medication'}`;
        return (
          <Box id={`medication-reminder-${medication.id}`} key={medication.id} sx={{ px: 1.4, py: 1.15, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', '&:last-child': { borderBottom: 0 } }}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 800 }}>{medication.name || 'Medication'}</Typography>
                <Typography variant="body2" color={needsConfirmation ? 'warning.main' : 'text.secondary'}>
                  {actionOnly && !savedSchedule ? formatReminderSchedule(schedule) : schedule.kind === 'prn' ? 'No scheduled reminder' : formatReminderSchedule(schedule)}
                </Typography>
              </Box>
              {schedule.kind === 'prn' ? <Box sx={{ px: 1.1, py: 0.45, borderRadius: 5, bgcolor: '#F1F5F3', color: 'text.secondary', fontSize: '0.78rem', fontWeight: 800 }}>As needed</Box> : null}
              {canManage && schedule.kind !== 'prn' ? (
                <Stack direction="row" spacing={0.35} alignItems="center">
                  {!actionOnly && schedule.active ? <Switch size="small" checked disabled={busy === medication.id} onChange={() => disableSchedule(schedule)} slotProps={{ input: { 'aria-label': `Turn off reminder for ${medication.name || 'medication'}` } }} /> : null}
                  <IconButton size="small" disabled={busy === medication.id} onClick={() => editSchedule(medication)} aria-label={reminderActionLabel} sx={{ bgcolor: actionOnly ? 'transparent' : 'action.hover', color: 'primary.main', border: actionOnly ? '1px solid #C9D8D2' : 0, borderRadius: actionOnly ? 1.5 : '50%', px: actionOnly ? 1.4 : undefined, width: actionOnly ? 'auto' : undefined }}>
                    {actionOnly ? <Typography sx={{ fontSize: '0.8rem', fontWeight: 850 }}>Add reminder</Typography> : savedSchedule ? <EditCalendarOutlinedIcon fontSize="small" /> : <AddAlarmOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              ) : null}
            </Stack>
            {editingId === medication.id && draft ? <MedicationReminderScheduleEditor schedule={draft} onChange={setDraft} onSave={saveSchedule} saving={busy === medication.id} /> : null}
          </Box>
        );
      })}
    </Stack>
  );

  const statusSummary = enabled
    ? activeRoundCount > 0
      ? `On · ${activeRoundCount} active round${activeRoundCount === 1 ? '' : 's'}${nextReminderTime ? ` · Next at ${nextReminderTime}` : ''}`
      : configuredCount > 0
        ? `On · ${configuredCount} reminder${configuredCount === 1 ? '' : 's'}${nextReminderTime ? ` · Next at ${nextReminderTime}` : ''}`
        : 'On · No reminders set'
    : 'Off';

  if (summaryOnly) {
    return (
      <Button
        fullWidth
        onClick={onOpen}
        aria-label="Open medication reminders"
        sx={{ minHeight: 68, px: 1.25, border: '1px solid #D9E2DE', borderRadius: 2.5, justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', color: 'text.primary' }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.92rem', fontWeight: 900, lineHeight: 1.2 }}>Medication Reminders</Typography>
          <Typography sx={{ mt: 0.15, color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.25 }}>{statusSummary}</Typography>
        </Box>
        <ChevronRightRoundedIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
      </Button>
    );
  }

  return (
    <Box sx={{ border: pageLayout ? 0 : '1px solid', borderColor: '#D9E2DE', borderRadius: pageLayout ? 0 : 2.5, bgcolor: 'background.paper', overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minHeight: 68, px: { xs: 1, sm: 1.35 } }}>
        {pageLayout ? <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.92rem', fontWeight: 900, lineHeight: 1.2 }}>{enabled ? 'Reminders on' : 'Reminders off'}</Typography>
          <Typography sx={{ mt: 0.15, color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.25 }}>{statusSummary.replace(/^(On|Off)\s*·?\s*/, '') || 'Schedules and history are kept'}</Typography>
        </Box> : <Button
          onClick={() => setOpen((value) => !value)} aria-expanded={open}
          sx={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', px: 0, py: 0.8, textAlign: 'left', textTransform: 'none', color: 'text.primary' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 900, lineHeight: 1.2 }}>
              Medication Reminders
            </Typography>
            <Typography sx={{ mt: 0.15, color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.2 }}>
              {statusSummary}
            </Typography>
          </Box>
        </Button>}
        <Switch
          checked={enabled}
          onChange={toggleGlobal}
          disabled={busy === 'global'}
          slotProps={{ input: { 'aria-label': enabled ? 'Turn medication notifications off' : 'Turn medication notifications on' } }}
          sx={{ flexShrink: 0 }}
        />
      </Stack>
      <Collapse in={pageLayout || open} unmountOnExit>
        <Stack spacing={2} sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {error ? <Alert severity="warning">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}
          {enabled && configuredCount === 0 && canManage && activeMedications.length > 0 ? (
            <Alert severity="info">Choose a medication below to set its reminder.</Alert>
          ) : null}
          <DailyMedicationCheck occurrences={todayOccurrences} focusedOccurrenceId={focusedOccurrenceId} onRecord={record} busyId={busy} />
          <MedicationRoundSetup childId={childId} medications={activeMedications} scheduleByMedicationId={scheduleByMedicationId} rounds={rounds} canManage={canManage} focusedMedicationId={focusedMedicationId} />
          {individualReminderMedications.length ? <Box>
            <Typography sx={{ color: '#85928D', fontSize: '0.78rem', fontWeight: 900, letterSpacing: '0.08em' }}>MEDICATION REMINDERS</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, mb: 0.8 }}>Individual schedules for {childName || 'this care recipient'}.</Typography>
            {renderMedicationRows(individualReminderMedications)}
          </Box> : null}
          {asNeededMedications.length ? <Box>
            <Typography sx={{ color: '#85928D', fontSize: '0.78rem', fontWeight: 900, letterSpacing: '0.08em', mb: 0.8 }}>AS NEEDED</Typography>
            {renderMedicationRows(asNeededMedications)}
          </Box> : null}
          {medicationsWithoutReminder.length ? <Box>
            <Typography sx={{ color: '#85928D', fontSize: '0.78rem', fontWeight: 900, letterSpacing: '0.08em' }}>NO REMINDER YET</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, mb: 0.8 }}>On the medication list, but nobody gets reminded.</Typography>
            {renderMedicationRows(medicationsWithoutReminder, true)}
          </Box> : null}
          {!activeMedications.length ? <Typography variant="body2">No active medications in the master list.</Typography> : null}

        </Stack>
      </Collapse>
    </Box>
  );
};

export default MedicationRemindersPanel;
